import {
  addDoc,
  updateDoc,
  collection,
  doc,
  getDoc,
  orderBy,
  query,
  runTransaction,
  where,
  getDocs
} from 'firebase/firestore';

import {
  chatType,
  negotiationStatus,
  screenType
} from '../../../../constants/enum';
import i18n from '../../../../i18n/configs';
import {
  sendNoticeMail,
  sendNoticeMailFromClientAgent
} from '../../../sendgrid';
import { db } from '../../config';
import { issuesRef } from '../issue';
import { mediasRef } from '../media';
import { userFindByCompanyId, usersRef } from '../user';

import type { NegotiationType } from './negotiationType';
import type { User } from 'firebase/auth';
import type {
  DocumentReference,
  DocumentSnapshot,
  CollectionReference,
  DocumentData,
  Query,
  QuerySnapshot
} from 'firebase/firestore';

export const negotiations = collection(db, 'negotiations');
export const negotiation = (negotiationId: string): DocumentReference =>
  doc(negotiations, negotiationId);
export const negotiationSnap = async (
  negotiationId: string
): Promise<DocumentSnapshot> => await getDoc(negotiation(negotiationId));
export const negotiationChat = (
  negotiationId: string
): CollectionReference<DocumentData> =>
  collection(negotiation(negotiationId), 'chats');

export const negotiationOrder = (
  negotiationId: string
): CollectionReference<DocumentData> =>
  collection(negotiation(negotiationId), 'orders');

export const chatOrderByCreated = (
  negotiationId: string
): Query<DocumentData> =>
  query(negotiationChat(negotiationId), orderBy('created', 'asc'));

export const negotiationsByClientId = (clientId: string): Query<DocumentData> =>
  query(negotiations, where('clientId', '==', clientId));

export const negotiationsByPublisherId = (
  publisherId: string
): Query<DocumentData> =>
  query(negotiations, where('publisherId', '==', publisherId));

export const negotiationsByAgentId = (agentId: string): Query<DocumentData> =>
  query(negotiations, where('agentId', '==', agentId));

export const negotiationsSnapByClientId = async (
  clientId: string
): Promise<QuerySnapshot<DocumentData>> =>
  await getDocs(negotiationsByClientId(clientId));

export const negotiationsSnapByPublisherId = async (
  publisherId: string
): Promise<QuerySnapshot<DocumentData>> =>
  await getDocs(negotiationsByPublisherId(publisherId));

export const negotiationsSnapByAgentId = async (
  agentId: string
): Promise<QuerySnapshot<DocumentData>> =>
  await getDocs(negotiationsByAgentId(agentId));

export const negotiationsRef = collection(db, 'negotiations');

// メディアへの問い合わせ
export const mediaInquiry = async (
  screen: number,
  uid: string,
  watch: (arg0: string) => any,
  mediaId: string,
  clientId: string
): Promise<string> => {
  const userRef = doc(usersRef, uid);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  const mediaRef = doc(mediasRef, mediaId);
  const mediaDoc = await getDoc(mediaRef);
  const mediaData = mediaDoc.data();

  let negotiationData = {};
  if (screen === screenType.client) {
    negotiationData = {
      clientId: userData?.companyId as string,
      clientMainUid: uid,
      clientMember: [],
      companyType: screen,
      created: new Date(),
      issueId: watch('issue') as string,
      mediaId,
      publisherId: mediaData?.companyId as string,
      publisherMainUid: '',
      publisherMember: [],
      status: negotiationStatus.inquiries,
      updated: new Date()
    };
  } else if (screen === screenType.agent) {
    negotiationData = {
      agentId: userData?.companyId as string,
      agentMainUid: uid,
      agentMember: [],
      clientId,
      companyType: screen,
      created: new Date(),
      issueId: watch('issue') as string,
      mediaId,
      publisherId: mediaData?.companyId as string,
      publisherMainUid: '',
      publisherMember: [],
      status: negotiationStatus.inquiries,
      updated: new Date()
    };
  }

  // negotiationsにコレクションを追加
  const addNegotiationDoc = await addDoc(negotiationsRef, negotiationData);
  const negotiationDoc = doc(negotiationsRef, addNegotiationDoc.id);

  // コレクションIdをドキュメントに追加
  await updateDoc(negotiationDoc, {
    negotiationId: addNegotiationDoc.id
  });

  // negotiationsのchatに追加(システム通知)
  const chatDataAsSystem = {
    chat: i18n.t('negotiations.systemMessage.inquiry'),
    created: new Date(),
    isRead: [],
    senderId: uid,
    type: chatType.system
  };

  const chatsRef = collection(
    db,
    'negotiations',
    addNegotiationDoc.id,
    'chats'
  );
  const addChatDocAsSystem = await addDoc(chatsRef, chatDataAsSystem);
  const chatDocAsSystem = doc(chatsRef, addChatDocAsSystem.id);
  await updateDoc(chatDocAsSystem, { chatId: addChatDocAsSystem.id });

  const categoryText = (i: number): string => {
    switch (i) {
      case 1:
        return i18n.t('inquiry.category.1');
      case 2:
        return i18n.t('inquiry.category.2');
      case 3:
        return i18n.t('inquiry.category.3');
      case 4:
        return i18n.t('inquiry.category.4');
      case 5:
        return i18n.t('inquiry.category.5');
      case 6:
        return i18n.t('inquiry.category.6');
      default:
        return '';
    }
  };

  // negotiationsのchatに追加(お問い合わせ内容)
  const chatDataAsContact = {
    chat: `【${i18n.t('inquiry.category.label')}】\n${categoryText(
      Number(watch('category'))
    )}\n\n【${i18n.t('inquiry.content')}】\n${watch('message') as string}`,
    created: new Date(),
    isRead: [],
    senderId: uid,
    type: chatType.contact
  };

  const addChatDocAsContact = await addDoc(chatsRef, chatDataAsContact);
  const chatDocAsContact = doc(chatsRef, addChatDocAsContact.id);
  await updateDoc(chatDocAsContact, { chatId: addChatDocAsContact.id });

  // negotiationsのinquiriesに追加
  const inquiryData = {
    category: Number(watch('category') as string),
    chatId: addChatDocAsContact.id,
    content: watch('message') as string,
    created: new Date(),
    senderId: uid
  };

  const inquiriesRef = collection(
    db,
    'negotiations',
    addNegotiationDoc.id,
    'inquiries'
  );
  const addInquiryDoc = await addDoc(inquiriesRef, inquiryData);
  const inquiryDoc = doc(inquiriesRef, addInquiryDoc.id);
  await updateDoc(inquiryDoc, { inquiryId: addInquiryDoc.id });

  const negotiationDocSnap = await getDoc(negotiationDoc);

  const usersQuery = await userFindByCompanyId(
    (negotiationDocSnap.data()?.publisherId as string) ?? ''
  );
  const userIds = usersQuery.map((document) => document.data().uid as string);
  sendNoticeMailFromClientAgent(
    '',
    userIds,
    `${watch('message') as string}`,
    negotiationDocSnap.data() as NegotiationType,
    'お問い合わせ',
    uid
  );

  return addNegotiationDoc.id;
};

// 与件いいね送信
export const sendIssueLike = async (
  uid: string,
  watch: (arg0: string) => any,
  issueId: string
): Promise<string> => {
  const userRef = doc(usersRef, uid);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  const issueRef = doc(issuesRef, issueId);
  const issueDoc = await getDoc(issueRef);
  const issueData = issueDoc.data();

  let negotiationData = {};
  if (issueData?.agentId && issueData?.agentId !== '') {
    negotiationData = {
      agentId: issueData?.agentId as string,
      agentMainUid: '',
      agentMember: [],
      clientId: issueData?.companyId as string,
      companyType: screenType.agent,
      created: new Date(),
      issueId,
      mediaId: watch('media') as string,
      publisherId: userData?.companyId as string,
      publisherMainUid: uid,
      publisherMember: [],
      status: negotiationStatus.like,
      updated: new Date()
    };
  } else {
    negotiationData = {
      clientId: issueData?.companyId as string,
      clientMainUid: '',
      clientMember: [],
      companyType: screenType.client,
      created: new Date(),
      issueId,
      mediaId: watch('media') as string,
      publisherId: userData?.companyId as string,
      publisherMainUid: uid,
      publisherMember: [],
      status: negotiationStatus.like,
      updated: new Date()
    };
  }

  // negotiationsにコレクションを追加
  const addNegotiationDoc = await addDoc(negotiationsRef, negotiationData);
  const negotiationDoc = doc(negotiationsRef, addNegotiationDoc.id);

  // コレクションIdをドキュメントに追加
  await updateDoc(negotiationDoc, {
    negotiationId: addNegotiationDoc.id
  });

  // negotiationsのchatに追加(システム通知)
  const chatDataAsSystem = {
    chat: i18n.t('negotiations.systemMessage.like'),
    created: new Date(),
    isRead: [],
    senderId: uid,
    type: chatType.system
  };

  const chatsRef = collection(
    db,
    'negotiations',
    addNegotiationDoc.id,
    'chats'
  );
  const addChatDocAsSystem = await addDoc(chatsRef, chatDataAsSystem);
  const chatDocAsSystem = doc(chatsRef, addChatDocAsSystem.id);
  await updateDoc(chatDocAsSystem, { chatId: addChatDocAsSystem.id });

  // negotiationsのchatに追加(お問い合わせ内容)
  const chatDataAsContact = {
    chat: `${watch('message') as string}`,
    created: new Date(),
    isRead: [],
    senderId: uid,
    type: chatType.like
  };

  const addChatDocAsContact = await addDoc(chatsRef, chatDataAsContact);
  const chatDocAsContact = doc(chatsRef, addChatDocAsContact.id);
  await updateDoc(chatDocAsContact, { chatId: addChatDocAsContact.id });

  // negotiationsのinquiriesに追加
  const likeData = {
    chatId: addChatDocAsContact.id,
    content: watch('message') as string,
    created: new Date(),
    senderId: uid
  };

  const likesRef = collection(
    db,
    'negotiations',
    addNegotiationDoc.id,
    'likes'
  );
  const addlikeDoc = await addDoc(likesRef, likeData);
  const likeDoc = doc(likesRef, addlikeDoc.id);
  await updateDoc(likeDoc, { likeId: addlikeDoc.id });
  const negotiationDocSnap = await getDoc(negotiationDoc);

  if (issueData?.agentId && issueData?.agentId !== '') {
    const usersQuery = await userFindByCompanyId(
      (negotiationDocSnap.data()?.agentId as string) ?? ''
    );
    const userIds = usersQuery.map((document) => document.data().uid as string);
    sendNoticeMail(
      '',
      userIds,
      `${watch('message') as string}`,
      negotiationDocSnap.data() as NegotiationType,
      '与件いいね送信',
      uid
    );
  } else {
    const usersQuery = await userFindByCompanyId(
      (negotiationDocSnap.data()?.clientId as string) ?? ''
    );
    const userIds = usersQuery.map((document) => document.data().uid as string);
    sendNoticeMail(
      '',
      userIds,
      `${watch('message') as string}`,
      negotiationDocSnap.data() as NegotiationType,
      '与件いいね送信',
      uid
    );
  }

  return addNegotiationDoc.id;
};

export async function approve(
  negotiationProp: NegotiationType,
  currentUser: User | null | undefined,
  screen: number
): Promise<void> {
  const negotiationRef = negotiation(negotiationProp?.negotiationId);

  await runTransaction(db, async (transaction) => {
    const negotiationDoc = await transaction.get(negotiationRef);

    if (!negotiationDoc.exists()) {
      throw new Error('negotiation not found');
    }

    let updatedData;

    if (screen === screenType.client) {
      if (negotiationDoc.data()?.clientMainUid) {
        updatedData = {
          status: negotiationStatus.likeApproved,
          updated: new Date()
        };
      } else {
        updatedData = {
          clientMainUid: currentUser?.uid,
          status: negotiationStatus.likeApproved,
          updated: new Date()
        };
      }
    } else if (screen === screenType.agent) {
      if (negotiationDoc.data()?.agentMainUid) {
        updatedData = {
          status: negotiationStatus.likeApproved,
          updated: new Date()
        };
      } else {
        updatedData = {
          agentMainUid: currentUser?.uid,
          status: negotiationStatus.likeApproved,
          updated: new Date()
        };
      }
    }

    transaction.update(negotiationRef, updatedData);

    const chatRef = doc(collection(negotiationRef, 'chats'));
    const chatData = {
      chat: 'メッセージが承認されました',
      created: new Date(),
      senderId: currentUser?.uid ?? '',
      type: chatType.system
    };
    transaction.set(chatRef, chatData);
  });

  sendNoticeMailFromClientAgent(
    negotiationProp?.publisherMainUid ?? '',
    negotiationProp?.publisherMember ?? [],
    'メッセージが承認されました',
    negotiationProp,
    'メッセージ承認',
    currentUser?.uid ?? ''
  );
}

export async function reject(
  negotiationProp: NegotiationType,
  currentUser: User | null | undefined,
  screen: number
): Promise<void> {
  const negotiationRef = negotiation(negotiationProp?.negotiationId);

  await runTransaction(db, async (transaction) => {
    const negotiationDoc = await transaction.get(negotiationRef);
    if (!negotiationDoc.exists()) {
      throw new Error('negotiation not found');
    }

    let updatedData;

    if (screen === screenType.client) {
      if (negotiationDoc.data()?.clientMainUid) {
        updatedData = {
          status: negotiationStatus.likeRejected,
          updated: new Date()
        };
      } else {
        updatedData = {
          clientMainUid: currentUser?.uid,
          status: negotiationStatus.likeRejected,
          updated: new Date()
        };
      }
    } else if (screen === screenType.agent) {
      if (negotiationDoc.data()?.agentMainUid) {
        updatedData = {
          status: negotiationStatus.likeRejected,
          updated: new Date()
        };
      } else {
        updatedData = {
          agentMainUid: currentUser?.uid,
          status: negotiationStatus.likeRejected,
          updated: new Date()
        };
      }
    }

    transaction.update(negotiationRef, updatedData);

    const chatRef = doc(collection(negotiationRef, 'chats'));
    const chatData = {
      chat: 'メッセージが拒否されました',
      created: new Date(),
      senderId: currentUser?.uid ?? '',
      type: chatType.system
    };
    transaction.set(chatRef, chatData);
  });

  sendNoticeMailFromClientAgent(
    negotiationProp?.publisherMainUid ?? '',
    negotiationProp?.publisherMember ?? [],
    'メッセージが拒否されました',
    negotiationProp,
    'メッセージ拒否',
    currentUser?.uid ?? ''
  );
}
